import {
  AnimationMixer,
  CanvasTexture,
  Color,
  Euler,
  LinearFilter,
  LoopOnce,
  MathUtils,
  Object3D,
  RepeatWrapping,
  Sprite,
  SpriteMaterial,
  Vector3,
} from 'three';
import PinMaterial from '../globe/PinMaterial';

export default class Reticle extends Object3D {
  constructor(assetLibrary, globeRadius, supportAnimations) {
    super();

    this.labelScale = 0.001 / window.devicePixelRatio;
    this.labelOffsetX = 0.1;
    this.labelOffsetY = 0.0;
    this.isValid = true;

    this.gltf = assetLibrary.get('pin/user');
    const model = this.gltf.scene;
    const material = new PinMaterial(false);
    material.rippleMap = assetLibrary.get('pin/ripple');
    material.rippleMap.wrapS = RepeatWrapping;
    material.pinSize = 1.0;
    material.exposure = 2.0;
    material.pinToSphere = 0.0;
    material.baseScale = 1.5;
    material.pinColor = new Color(0xffffff);

    this.supportAnimations = supportAnimations;
    if (supportAnimations) material.skinning = true;
    this.material = material;
    model.children[0].children[1].material = material;
    this.add(model);
    this.globeRadius = globeRadius;
    this.viewAlign();

    model.setRotationFromEuler(new Euler(-Math.PI * 0.5, 0, 0));
    model.scale.set(0.01, 0.01, 0.01);

    this.bone = model.children[0].children[0].children[1];

    this.mixer = new AnimationMixer(this.gltf.scene);
    this.action = this.mixer.clipAction(this.gltf.animations[0]);
    this.action.loop = LoopOnce;
    this.action.clampWhenFinished = true;

    this._animationPlayed = false;
    this._animTime = 0;
    this._white = new Color(0xffffff);
    this._pinColor = new Color(0xffffff);
    this.movementScale = 1.0;
    this.targetMovementScale = 1.0;
  }

  update(dt, movement) {
    this.mixer.update(dt);
    this.material.phase += 0.2 * dt;

    const validFactor = this.isValid ? 1.0 : 0.0;
    this.material.validFactor = MathUtils.lerp(this.material.validFactor, validFactor, 0.1);

    // tween the pin color, synced to the grow animation
    if (this.action) {
      const { time } = this.action;
      if (this._animTime !== time) {
        const t = time / this.action.getClip().duration;
        this.material.pinColor.copy(this._white).lerp(this._pinColor, t);
        this._animTime = time;
      }
    } else {
      // nothing to lerp with if no animations supported, so just copy
      if (this._animationPlayed) {
        this.material.pinColor.copy(this._pinColor);
      } else {
        this.material.pinColor.copy(this._white);
      }
    }

    this.targetMovementScale = Math.min(1.0 + movement * 2.0, 2.0);
    this.movementScale = MathUtils.lerp(this.movementScale, this.targetMovementScale, 0.05);
    this.material.pinSize = this.movementScale;

    // world z
    if (this.labelSprite) {
      const z = this.labelSprite.matrixWorld.elements[14];
      // fade out the label when it's about to disappear "behind the globe"
      // (otherwise we'd have depth clipping against the globe)
      const targetAlpha = z < 0.5 ? 0.0 : 1.0;
      this.labelSprite.material.opacity = MathUtils.lerp(
        this.labelSprite.material.opacity,
        targetAlpha,
        0.3
      );
    }

    if (!this.supportAnimations) {
      this.gltf.scene.children[0].scale.copy(this.bone.scale);
    }
  }

  viewAlign() {
    this.position.x = 0;
    this.position.y = 0;
    this.position.z = this.globeRadius;
    this.lookAt(new Vector3(0, 0, 0));
  }

  setScale(scale = [0.01, 0.01, 0.01]) {
    const model = this.gltf.scene;
    // let s = new Vector3(scale[0], scale[1], scale[2]);
    model.scale.set(scale[0], scale[1], scale[2]);
  }

  playAnimation() {
    if (this._animationPlayed === false) {
      // console.log('playing animation');
      if (this.action) {
        this.action.time = 0;
        this.action.play();
      }
      this._animationPlayed = true;
    }
  }

  resetAnimation() {
    // console.log('reset animation');
    if (this.action) {
      this.action.time = 0;
      this.action.stop();
    }
    this._animationPlayed = false;
  }

  get pinColor() {
    return this._pinColor;
  }

  set pinColor(value) {
    this._pinColor.set(value);
  }

  removeLabel() {
    if (this.labelSprite) {
      this.remove(this.labelSprite);
      this.labelSprite = null;
    }
  }

  setLabel(canvas) {
    this.removeLabel();
    const tex = new CanvasTexture(canvas);
    tex.minFilter = LinearFilter;
    const material = new SpriteMaterial({ color: 0xffffff });
    material.map = tex;
    material.depthTest = false;
    // make it fade in
    material.opacity = 0.0;
    const sprite = new Sprite(material);
    sprite.baseWidth = this.labelScale * canvas.width;
    sprite.baseHeight = this.labelScale * canvas.height;
    sprite.position.z = -0.03;
    sprite.center.x = 0 - this.labelOffsetX;
    sprite.center.y -= this.labelOffsetY;
    sprite.layers.set(2);
    sprite.scale.set(sprite.baseWidth, sprite.baseHeight, 1.0);
    // sprite.visible = false;
    this.add(sprite);
    this.labelSprite = sprite;
  }
}
